import _ from 'lodash';
import { AppConfigService } from '../../../services/configuration.service';
import { appRsMapConstants } from '../constants/rs-map.constants';
import { appRsMapOptionsBase } from '../constants/rs-map.options.constants';
import { TripStopTypeEnum } from '../enum/generic.enums';
import { IAvailableProductsForGrid, IAvailableProductsForGridExtra } from '../interfaces/generic.interfaces';
import { IRsMapOptions, MapAvailablePanelsType } from '../interfaces/mapOptions.interface';
import { IOrderProductDTO } from '../interfacesDTO/order.interfaces.dto';
import { ITripStopDTO, ITripStopProductDTO } from '../interfacesDTO/tripStop.interfaces.dto';
import { IUserAvailableProductsDTO } from '../interfacesDTO/userSettings.interfaces.dto';
import { TripColorType } from './rs-map-trip.service';

// Ritorna le opzioni di configurazione di rs-map
export const appRsMapOptions = () => (AppConfigService.settings["rsMapOptions"]) as IRsMapOptions ?? appRsMapOptionsBase;

/**
 * Converte una stringa di sile css in JSON
 * @param css
 */
export function cssStringToJSON(css: string): any {
  const json: Record<string, string> = {};
  (css || "").split(";").forEach(x => {
    if (x == "" || x == null) {
      return;
    }
    const splitted = x.split(":");
    json[splitted[0]] = splitted[1];
  });
  return json;
}

/**
 * Ritorna il prodotto per chiave impostata
 * @param products
 * @param id
 * @param tag
 */
export function getProductBy(products: (ITripStopProductDTO | IOrderProductDTO)[], id: string, tag: string): ITripStopProductDTO | IOrderProductDTO {
  const product = appRsMapOptions().product.getBy === 'id' ? _.find(products, (product) => product.productId === id) : _.find(products, (product) => product.productTag === tag);
  if (!product) {
    throw new Error('Product not found');
  }
  return product;
}

/**
 * field="<appRsMapConstants.productFieldPrefix><id>" è costruito così perché il prodotto potrebbe avere id numerico (o avere un id che comincia con una cifra)
 * ciò non è valido poiché il title deve essere di tipo stringa e non cominciare con una cifra o caratteri speciali
 */
export function getProductColumns(products: IUserAvailableProductsDTO[], extraColumns: IAvailableProductsForGridExtra[] = []): IAvailableProductsForGrid[] {
  const productColumns = _.map(products, x => _.merge({}, x, {
    cssStyle: x.cssStyle != null && (x.cssStyle as any instanceof Object) ? x.cssStyle : cssStringToJSON(x.cssStyle || ''),
    editable: true,
    field: `${appRsMapConstants.productFieldPrefix}${x[appRsMapOptions().product.getBy]}`,
    format: "n0",
    sortable: false
  }));

  if (!extraColumns?.length) {
    return productColumns;
  }

  const ret: IAvailableProductsForGrid[] = [];
  _.forEach(productColumns, x => {
    ret.push(x);
    _.forEach(extraColumns, ex => {
      const value = _.merge({}, x, ex);
      value.field = `${ex.field}_${x[appRsMapOptions().product.getBy]}`
      if (value.titleAppend === "pre") {
        value.title = `${x.tag} ${value.title}`;
      } else {
        value.title += ` ${x.tag}`;
      }
      ret.push(value)
    });
  });

  return ret;
}

/**
 * Ritorna la quantità/valore per prodotto
 * @param stopType
 * @param products
 * @param column
 * @param uom
 * @param property
 * @param defaultValue
 * @returns
 */
export function getProductValue(stopType: TripStopTypeEnum, products: (ITripStopProductDTO | IOrderProductDTO)[], column: IAvailableProductsForGrid, uom: boolean | string, property = 'quantityUOM', defaultValue: any = 0): number | string {
  switch (stopType) {
    case TripStopTypeEnum.BREAK:
    case TripStopTypeEnum.LOAD:
    case TripStopTypeEnum.OTHER:
    case TripStopTypeEnum.RETURN:
    case TripStopTypeEnum.SERVICE:
    case TripStopTypeEnum.START: {
      return "";
    }
    default: {
      const productFound = getProductBy(products, column.id, column.tag);

      if (productFound) {
        switch (uom) {
          case true:
            return `${(productFound as any)[property]} ${productFound.uomTag || '?'}`.trim();
          case false:
          case null:
          case undefined:
            return (productFound as any)[property];
          default:
            return `${(productFound as any)[property]} ${uom}`.trim();
        }
      } else {
        switch (uom) {
          case true:
            return `${defaultValue}`;
          case false:
          case null:
          case undefined:
            return defaultValue;
          default:
            return `${defaultValue} ${uom}`.trim();
        }
      }
    }
  }
}

/**
 * Ritorna il titolo della colonna prodotto
 * @param products
 * @param productTag
 */
export function getProductValueTitle(stopList: ITripStopDTO[], column: IUserAvailableProductsDTO): string {
  const productList = _.map(stopList, x => Object.values(x.products));

  return appRsMapOptions().product.getBy === 'id' ?
    `${column.tag} (${_.sumBy(_.flatten(_.filter(productList, x => x != null)), x => {
      return (x.productId === column.id) ? (x.quantityUOM ?? 0) : 0;
    })})` :
    `${column.tag} (${_.sumBy(_.flatten(_.filter(productList, x => x != null)), x => {
      return (x.productTag === column.tag) ? (x.quantityUOM ?? 0) : 0;
    })})`;
}

/**
 * Ritorna la classe da applicare alla visualizzazione della saturazione
 * @param truckVolume
 * @param tripVolume
 */
export function getSaturationClass(truckVolume: number, tripVolume: number): string {
  const perc: number = tripVolume / truckVolume;
  if (perc >= 0.999) {
    return "btn-danger";
  } else if (perc >= 0.8) {
    return "btn-warning";
  }

  return "btn-success";
}

/**
 * Ritorna l'icona per il tipo di fermata
 * @param stopType
 */
export function getStopIcon(stopType: TripStopTypeEnum): string {
  switch (stopType) {
    case TripStopTypeEnum.BREAK: {
      return "far fa-pause-circle text-info";
    }
    case TripStopTypeEnum.DELIVERY: {
      return "fas fa-download";
    }
    case TripStopTypeEnum.DELIVERYBUFFER: {
      return "fab fa-stack-overflow";
    }
    case TripStopTypeEnum.LOAD: {
      return "fas fa-truck-loading text-info";
    }

    case TripStopTypeEnum.RETURN: {
      return "fas fa-flag-checkered text-danger";
    }
    case TripStopTypeEnum.SERVICE: {
      return "fas fa-wrench text-info";
    }
    case TripStopTypeEnum.START: {
      return "far fa-play-circle text-success";
    }
    case TripStopTypeEnum.OTHER:
    default: {
      return "far fa-question-circle text-warning";
    }
  }
}

/**
 * Ritorna l'etichetta da tradurre per il tipo di fermata
 * @param stopType
 */
export function getStopI18nLabel(stopType: TripStopTypeEnum): string {
  switch (stopType) {
    case TripStopTypeEnum.BREAK: {
      return "RSMAP.Sosta";
    }
    case TripStopTypeEnum.DELIVERY: {
      return "RSMAP.Consegna";
    }
    case TripStopTypeEnum.DELIVERYBUFFER: {
      return "RSMAP.Consegna di scorta";
    }
    case TripStopTypeEnum.LOAD: {
      return "RSMAP.Carico prodotto";
    }
    case TripStopTypeEnum.RETURN: {
      return "RSMAP.Rientro";
    }
    case TripStopTypeEnum.SERVICE: {
      return "RSMAP.Service";
    }
    case TripStopTypeEnum.START: {
      return "RSMAP.Partenza";
    }
    case TripStopTypeEnum.OTHER:
    default: {
      return "RSMAP.Altro";
    }
  }
}

/** Ritorna la lista dei colori disponibili per le tracce di routing */
export function getTripColorTypes(): Array<TripColorType> {
  return [
    { id: 0, color: appRsMapOptions().trip.defaultFirstTripColor ?? '#669DF6', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // Azzurro stile Google Map http://www.colorhexa.com/669DF6
    { id: 1, color: '#0074D9', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // Blue http://www.colorhexa.com/0074D9
    { id: 2, color: '#7FDBFF', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // Aqua http://www.colorhexa.com/7FDBFF
    { id: 3, color: '#39CCCC', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // Teal http://www.colorhexa.com/39CCCC
    { id: 4, color: '#3D9970', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // Olive http://www.colorhexa.com/3D9970
    { id: 5, color: '#2ECC40', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // Green http://www.colorhexa.com/2ECC40
    { id: 6, color: '#01FF70', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // Lime http://www.colorhexa.com/01FF70
    { id: 7, color: '#FF851B', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // Orange http://www.colorhexa.com/FF851B
    { id: 8, color: '#111111', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // Almost black http://www.colorhexa.com/111111
    { id: 9, color: '#85144B', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // Maroon http://www.colorhexa.com/85144B
    { id: 10, color: '#F012BE', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // Fuchsia http://www.colorhexa.com/F012BE
    { id: 11, color: '#B10DC9', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // Purple http://www.colorhexa.com/B10DC9
    { id: 12, color: '#AAAAAA', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // Gray http://www.colorhexa.com/AAAAAA
    { id: 13, color: '#DDDDDD', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // Silver http://www.colorhexa.com/DDDDDD
    { id: 14, color: '#666666', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // Darkgray http://www.colorhexa.com/DDDDDD
    { id: 15, color: '#7FFF00', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // CharTreuse http://www.colorhexa.com/7FFF00
    { id: 16, color: '#E6E6FA', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // Lavander http://www.colorhexa.com/E6E6FA
    { id: 17, color: '#191970', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // MidnightBlue http://www.colorhexa.com/191970
    { id: 18, color: '#FFDC00', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] }, // yellow http://www.colorhexa.com/FFDC00
    { id: 19, color: '#006400', inuse: function () { return this.tripIdList?.length > 0; }, tripIdList: [] } // Darkgreen http://www.colorhexa.com/006400
  ];
}

/** Ritorna l'offset della pagina di wrapper */
export function getWrapperPageOffSet(): number {
  const dialog: HTMLElement | null = document.querySelector("kendo-dialog .k-window") || document.querySelector("kendo-window .k-window");

  if (dialog && dialog.querySelector(".leaflet-container")) { // Serve per stabilire se il componente leaflet (la mappa) sia visualizzata all'interno di una dialog/window o meno
    return (window.innerHeight - dialog.offsetHeight) + ((dialog.querySelector(".k-dialog-titlebar") as HTMLElement)?.offsetHeight || 0) + ((dialog.querySelector("kendo-dialog-actions") as HTMLElement)?.offsetHeight || 0) + (document.querySelector("app-rs-map-leaflet") as HTMLElement).offsetTop;
  } else {
    return ((document.querySelector("c-header") as HTMLElement)?.offsetHeight || 0) + ((document.querySelector("app-rs-map-toolbar > div") as HTMLElement)?.offsetHeight || 0)
  }
}

/**
 * Determina se un pannello sia disponibile
 * @param panelId
 * @returns
 */
export function isPanelAvailable(panelId: MapAvailablePanelsType): boolean {
  return _.some(appRsMapOptions().panels, x => x.panel === panelId);
}

/**
 * Determina se la fermata sia un terminal (partenza, carico o rientro)
 * @param stopType
 */
export function isStopTerminal(stopType: TripStopTypeEnum): boolean {
  return _.includes([TripStopTypeEnum.START, TripStopTypeEnum.LOAD, TripStopTypeEnum.RETURN], stopType);
}

/**
 * Strip HTML from Text JavaScript
 * ref: https://stackoverflow.com/a/47140708
 * @param text
 */
export function stripHtml(text: string) {
  if (text == null || typeof (text) === "number") {
    return text;
  }

  const doc = new DOMParser().parseFromString(text, 'text/html');
  return doc.body.textContent || "";
}
